/**
 * The legacy Amplitude `trackEvent` vocabulary, translated to PostHog's.
 *
 * The desktop client spells its ~80 event names for Amplitude's console
 * (`Staged Action`, `Settings - Models`, `HLC Message Quote Quoted`) behind one
 * `trackEvent(name, props)` interface with several hundred call sites.
 * Translating here rather than at each of them is what keeps the two streams
 * from drifting.
 *
 * The translation is mechanical. It mirrors Amplitude's taxonomy, including the
 * parts ../../../.cursor/rules/analytics.mdc calls a mistake, like the seven
 * flat `Sidepanel *` events. Dual-writing is only useful while a PostHog number
 * can be checked against the Amplitude number it replaces. Collapsing names
 * into a better taxonomy needs the dashboards in hand, event by event.
 */

import type { AnalyticsProperties } from './types'

/** Sent instead of an empty name, so a bad call is visible rather than silent. */
export const UNKNOWN_LEGACY_EVENT = 'unknown_event'

/** Amplitude's own instrumentation prefix, e.g. `[Amplitude] Page Viewed`. */
const VENDOR_PREFIX = /^\s*\[amplitude\]\s*/i

/**
 * Splits camelCase, acronyms included: `HTTPRequest` becomes `HTTP_Request`,
 * not `H_T_T_P_Request`. Most legacy names are space-separated already, but a
 * few call sites pass the name through a variable.
 */
const ACRONYM_BOUNDARY = /([A-Z]+)([A-Z][a-z])/g
const WORD_BOUNDARY = /([a-z\d])([A-Z])/g

export function legacyEventName(name: string): string {
  // Runs at the call site, outside the analytics client's own try/catch, so a
  // throw here would take out the click handler that tracked the event.
  if (typeof name !== 'string') {
    return UNKNOWN_LEGACY_EVENT
  }

  // Stripping the sigil from `$pageview` or `$identify` would turn an event
  // PostHog treats specially into an ordinary custom one with a similar name.
  if (name.startsWith('$')) {
    return name
  }

  const normalized = name
    .replace(VENDOR_PREFIX, '')
    .replace(ACRONYM_BOUNDARY, '$1_$2')
    .replace(WORD_BOUNDARY, '$1_$2')
    .toLowerCase()
    // Everything that is not a name character becomes a separator: spaces, the
    // ` - ` in `Settings - Models`, punctuation.
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')

  return normalized || UNKNOWN_LEGACY_EVENT
}

/**
 * The legacy interface types its property bag as `object`, which TypeScript
 * will not pass to a `Record<string, unknown>` parameter even though every
 * caller supplies a plain object literal. Asserted here so the dual-writing
 * call sites stay free of casts.
 */
export const legacyEventProperties = (properties?: object): AnalyticsProperties | undefined =>
  properties as AnalyticsProperties | undefined
